'use client';

import { useEffect, useState } from 'react';
import { Accordion, AccordionControl, AccordionItem, AccordionPanel, Container, Grid, GridCol } from '@mantine/core';
import classes from './JumpMenu.module.scss';

function slugifyHeading(text) {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
}

function collectHeadingLinks() {
    const root = document.querySelector('#content') || document.body;
    const sections = Array.from(root.querySelectorAll('section')).filter((section) => !section.closest('[data-jump-menu-root="true"]'));
    const usedIds = new Set();

    return sections
        .map((section, index) => {
            const heading = section.querySelector('h2');
            if (!heading) {
                return null;
            }

            const label = heading.textContent?.trim();

            if (!label) {
                return null;
            }

            let id = section.id || slugifyHeading(label) || `section-${index + 1}`;
            let uniqueId = id;
            let duplicateCount = 2;

            while (usedIds.has(uniqueId) || document.querySelectorAll(`#${CSS.escape(uniqueId)}`).length > 1) {
                uniqueId = `${id}-${duplicateCount}`;
                duplicateCount += 1;
            }

            id = uniqueId;
            usedIds.add(id);

            if (section.id !== id) {
                section.id = id;
            }

            return { id, label };
        })
        .filter(Boolean);
}

export function JumpMenu({ data }) {
    const [items, setItems] = useState([]);
    const headingLevel = data.headingLevel ? parseInt(data.headingLevel.charAt(1), 10) : 2;

    useEffect(() => {
        const updateItems = () => {
            setItems(collectHeadingLinks());
        };

        updateItems();
        const rafId = window.requestAnimationFrame(updateItems);
        const timeoutId = window.setTimeout(updateItems, 150);

        window.addEventListener('load', updateItems);

        return () => {
            window.cancelAnimationFrame(rafId);
            window.clearTimeout(timeoutId);
            window.removeEventListener('load', updateItems);
        };
    }, []);

    const onItemClick = (event, id) => {
        event.preventDefault();
        const section = document.getElementById(id);

        if (!section) {
            return;
        }

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        section.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        window.history.replaceState(null, '', `#${id}`);
    };

    if (!items.length) {
        return null;
    }

    return (
        <section className={classes.section} data-jump-menu-root="true">
            <Container size="lg">
                <Grid>
                    <GridCol span={12}>
                        <Accordion radius="md" bg="gray.1" className={classes.accordion}>
                            <AccordionItem value="jump-menu">
                                <AccordionControl className={classes.accordionControl}>{data.title || 'On this page'}</AccordionControl>
                                <AccordionPanel bg="white">
                                    <ul className={classes.linkList}>
                                        {items.map((item) => (
                                            <li key={item.id} className={classes.linkItem}>
                                                <a href={`#${item.id}`} onClick={(event) => onItemClick(event, item.id)}>
                                                    {item.label}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                    </GridCol>
                </Grid>
            </Container>
        </section>
    );
}